import { defineStore } from 'pinia'
import Cookies from 'js-cookie'
import CookieCategory from '@/enum/CookieCategory'

const JP_MODES = ['jp', 'test-jp']

// Cookie项接口
export interface CookieItem {
  id: CookieCategory
  title: string
  description: string
  /** 是否必选项 */
  required: boolean
  /** 是否选中 */
  checked: boolean
  /** 是否展开 */
  expanded: boolean
  /** 用于控制具体功能的标识 */
  features: string[]
}

// Cookie设置状态接口
export interface CookieSettings {
  isBannerVisible: boolean
  isSettingVisible: boolean
  cookieItems: CookieItem[]
  hasUserConsent: boolean
  lastUpdated: number
  /** 版本号，用于后续更新 */
  version: string
}

export const useCookieStore = defineStore('cookie', {
  state: (): CookieSettings => ({
    isBannerVisible: false,
    isSettingVisible: false,
    hasUserConsent: false,
    lastUpdated: 0,
    version: '1.0.0',
    cookieItems: [
      {
        id: CookieCategory.STRICTLY_NECESSARY,
        title: 'strictly',
        description: 'strictlyDesc',
        required: true,
        checked: true,
        expanded: false,
        features: ['basic_navigation', 'security', 'session_management']
      },
      {
        id: CookieCategory.PERFORMANCE,
        title: 'performance',
        description: 'performanceDesc',
        required: false,
        checked: false,
        expanded: false,
        features: ['analytics', 'performance_monitoring', 'traffic_analysis']
      },
      {
        id: CookieCategory.FUNCTIONAL,
        title: 'functional',
        description: 'functionalDesc',
        required: false,
        checked: false,
        expanded: false,
        features: ['personalization', 'user_preferences', 'enhanced_features']
      },
      {
        id: CookieCategory.TARGETING,
        title: 'targeting',
        description: 'targetingDesc',
        required: false,
        checked: false,
        expanded: false,
        features: ['advertising', 'remarketing', 'social_media_integration']
      }
    ]
  }),
  getters: {
    // 获取所有已启用的cookie类别
    enabledCookies: (state) => {
      return state.cookieItems.filter(item => item.checked)
    },
    // 检查特定类别是否启用
    isCookieEnabled: (state) => {
      return (category: CookieCategory) => {
        const item = state.cookieItems.find(item => item.id === category)
        return item ? item.checked : false
      }
    },
    // 获取cookie设置摘要
    cookieSummary: (state) => {
      const enabled = state.cookieItems.filter(item => item.checked).length
      const total = state.cookieItems.length
      return `${enabled}/${total} categories enabled`
    },
    // 检查特定功能是否启用
    isFeatureEnabled: (state) => {
      return (feature: string) => {
        return state.cookieItems.some(item => 
          item.checked && item.features.includes(feature)
        )
      }
    },
    // 获取所有启用的功能
    enabledFeatures: (state) => {
      const features: string[] = []
      state.cookieItems.forEach(item => {
        if (item.checked) {
          features.push(...item.features)
        }
      })
      return features
    },
  },
  actions: {
    // 显示横幅
    showBanner() {
      this.isBannerVisible = true
    },

    // 隐藏横幅
    hideBanner() {
      this.isBannerVisible = false
    },

    // 显示设置弹窗
    showSetting() {
      this.isSettingVisible = true
    },

    // 隐藏设置弹窗
    hideSetting() {
      this.isSettingVisible = false
    },

    // 切换cookie项状态
    toggleCookieItem(category: CookieCategory, checked: boolean) {
      const item = this.cookieItems.find(item => item.id === category)
      if (item && !item.required) {
        item.checked = checked
      }
    },

    // 接受所有cookie
    acceptAll(type) {
      this.cookieItems.forEach(item => {
        item.checked = true
      })
      this.saveSettings()
      this.hideBanner()
      if (type === 'setting') this.hideSetting()
    },

    // 拒绝所有非必要cookie
    rejectAll(type) {
      this.cookieItems.forEach(item => {
        item.checked = item.required
      })
      this.saveSettings()
      this.hideBanner()
      if (type === 'setting') this.hideSetting()
    },

    // 确认用户选择
    confirmChoice() {
      this.saveSettings()
      this.hideSetting()
      this.hideBanner()
    },

    // 保存设置到localStorage和cookie
    saveSettings() {
      this.hasUserConsent = true
      this.lastUpdated = Date.now()
      
      // 1、保存到localStorage
      // localStorage.setItem('cookieSettings', JSON.stringify({
      //   cookieItems: this.cookieItems,
      //   hasUserConsent: this.hasUserConsent,
      //   lastUpdated: this.lastUpdated,
      //   version: this.version
      // }))

      // 2、保存到cookie（用于跨页面共享）
      Cookies.set('cookieConsent', JSON.stringify({
        cookieItems: this.cookieItems,
        hasUserConsent: this.hasUserConsent,
        lastUpdated: this.lastUpdated,
        version: this.version
      }), { expires: 365 }) // 保存1年

      // 3、应用cookie设置到第三方服务
      this.controlThirdPartyServices()
    },

    // 从localStorage或cookie加载设置
    loadSettings() {
      // 1、优先从localStorage加载
      // const localStorageSettings = localStorage.getItem('cookieSettings')
      // if (localStorageSettings) {
      //   try {
      //     const settings = JSON.parse(localStorageSettings)
      //     this.cookieItems = settings.cookieItems || this.cookieItems
      //     this.hasUserConsent = settings.hasUserConsent || false
      //     this.lastUpdated = settings.lastUpdated || 0
      //     this.version = settings.version || this.version
          
      //     // 加载后立即应用设置
      //     if (this.hasUserConsent) {
      //       this.controlThirdPartyServices()
      //     }
      //     return
      //   } catch (e) {
      //     console.warn('Failed to parse localStorage cookie settings: ', e)
      //   }
      // }

      // 2、从cookie加载
      const cookieConsent = Cookies.get('cookieConsent')
      if (cookieConsent) {
        try {
          const settings = JSON.parse(cookieConsent)
          this.cookieItems = settings.cookieItems || this.cookieItems
          this.hasUserConsent = settings.hasUserConsent || false
          this.lastUpdated = settings.lastUpdated || 0
          this.version = settings.version || this.version
          
          // 加载后立即应用设置
          if (this.hasUserConsent) {
            this.controlThirdPartyServices()
          }
        } catch (e) {
          console.warn('Failed to parse cookie consent: ', e)
        }
      }
    },

    // 重置设置
    resetSettings() {
      this.cookieItems.forEach(item => {
        item.checked = item.required
      })
      this.hasUserConsent = false
      this.lastUpdated = 0
      this.version = '1.0.0'
      // 移除设置的Cookie
      // localStorage.removeItem('cookieSettings')
      Cookies.remove('cookieConsent')
    },

    // 初始化（在应用启动时调用）
    init() {
      this.loadSettings()
      
      // 如果用户还没有同意，延迟显示横幅（注意：这里只在应用首次启动时显示，后续的显示由路由守卫控制）
      if (!this.hasUserConsent) {
        setTimeout(() => {
          this.showBanner()
        }, 1000)
      }
    },

    // 根据cookie设置控制第三方服务
    controlThirdPartyServices() {
      // 1、控制Google Analytics 和 Google Tag Manager (Performance Cookies)
      if (this.isCookieEnabled(CookieCategory.PERFORMANCE)) {
        this.enableGoogleAnalytics()
        this.enableGoogleTagManager()
      } else {
        this.disableGoogleAnalytics()
        this.disableGoogleTagManager()
      }

      // 2、控制Facebook Pixel 和 Twitter Pixel (Targeting Cookies)
      if (this.isCookieEnabled(CookieCategory.TARGETING)) {
        this.enableFacebookPixel()
        if (JP_MODES.includes(import.meta.env.MODE)) this.enableTwitterPixel()
      } else {
        this.disableFacebookPixel()
        if (JP_MODES.includes(import.meta.env.MODE)) this.disableTwitterPixel()
      }

      // 3、控制功能特性 (Functional Cookies)
      if (this.isCookieEnabled(CookieCategory.FUNCTIONAL)) {
        this.enableFunctionalFeatures()
      } else {
        this.disableFunctionalFeatures()
      }
    },

    // 启用Google Analytics
    enableGoogleAnalytics() {
      if (typeof window !== 'undefined') {
        // 如果是jp模式，使用新的Google Ads跟踪
        if (JP_MODES.includes(import.meta.env.MODE)) {
          // 动态加载Google Ads脚本
          if (!window.gtag) {
            this.loadGoogleAdsScript()
          }
          
          // 更新consent状态
          if (window.gtag) {
            window.gtag('consent', 'update', {
              'analytics_storage': 'granted',
              'ad_storage': 'granted'
            })
          }
        } else {
          // 非jp模式，保持原有逻辑
          // 动态加载Google Analytics脚本
          if (!window.gtag) {
            this.loadGoogleAnalyticsScript()
          }
          
          // 更新consent状态
          if (window.gtag) {
            window.gtag('consent', 'update', {
              'analytics_storage': 'granted',
              'ad_storage': 'denied', // 默认禁用广告存储
              'functionality_storage': 'denied' // 默认禁用功能存储
            })
          }
        }
      }
    },

    // 禁用Google Analytics
    disableGoogleAnalytics() {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'functionality_storage': 'denied'
        })
        
        // 可选：完全移除Google Analytics
        this.removeGoogleAnalyticsScript()
      }
    },

    // 动态加载Google Ads脚本（jp模式专用）
    loadGoogleAdsScript() {
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17543232588'
      document.head.appendChild(script)
      
      script.onload = () => {
        // 初始化gtag
        window.dataLayer = window.dataLayer || []
        window.gtag = function() { window.dataLayer.push(arguments) }
        window.gtag('js', new Date())
        window.gtag('config', 'AW-17543232588')
      }
    },

    // 动态加载Google Analytics脚本
    loadGoogleAnalyticsScript() {
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-SRMX45JSQ8'
      document.head.appendChild(script)
      
      script.onload = () => {
        // 初始化gtag
        window.dataLayer = window.dataLayer || []
        window.gtag = function() { window.dataLayer.push(arguments) }
        window.gtag('js', new Date())
        window.gtag('config', 'G-SRMX45JSQ8', {
          'consent_mode': 'advanced'
        })
      }
    },

    // 移除Google Analytics脚本
    removeGoogleAnalyticsScript() {
      const scripts = document.querySelectorAll('script[src*="googletagmanager"]')
      scripts.forEach(script => script.remove())
      
      // 清除dataLayer
      if (window.dataLayer) {
        window.dataLayer = []
      }
    },

    // 启用Facebook Pixel
    enableFacebookPixel() {
      // 这里可以添加Facebook Pixel的启用逻辑
      console.log('Facebook Pixel enabled')
    },

    // 禁用Facebook Pixel
    disableFacebookPixel() {
      // 这里可以添加Facebook Pixel的禁用逻辑
      console.log('Facebook Pixel disabled')
    },

    // 启用Twitter Pixel
    enableTwitterPixel() {
      // 启用Twitter跟踪
      if (typeof window !== 'undefined' && !window.twq) {
        this.loadTwitterPixelScript()
      }
    },

    // 禁用Twitter Pixel
    disableTwitterPixel() {
      // 禁用Twitter跟踪
      if (typeof window !== 'undefined' && window.twq) {
        this.removeTwitterPixelScript()
      }
    },

    // 动态加载Twitter Pixel脚本（jp模式专用）
    loadTwitterPixelScript() {
      if (typeof window === 'undefined') {
        return
      }

      const script = document.createElement('script')
      script.innerHTML = `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');twq('config','pinwi');`
      document.head.appendChild(script)
    },

    // 移除Twitter Pixel脚本
    removeTwitterPixelScript() {
      if (typeof window === 'undefined') {
        return
      }

      const scripts = document.querySelectorAll('script[src*="ads-twitter.com"]')
      scripts.forEach(script => script.remove())
    },

    // 启用功能特性
    enableFunctionalFeatures() {
      // 启用用户偏好记忆
      this.enableUserPreferences()
    },

    // 禁用功能特性
    disableFunctionalFeatures() {
      // 禁用用户偏好记忆
      this.disableUserPreferences()
    },

    // 启用用户偏好记忆
    enableUserPreferences() {
      // 允许保存用户设置到localStorage
      console.log('User preferences enabled')
    },

    // 禁用用户偏好记忆
    disableUserPreferences() {
      // 清除已保存的用户偏好
      const keysToRemove = ['userPreferences', 'userSettings', 'theme', 'language']
      keysToRemove.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key)
        }
      })
      console.log('User preferences disabled and cleared')
    },

    // 启用Google Tag Manager
    enableGoogleTagManager() {
      if (typeof window !== 'undefined') {
        // 如果GTM还没有加载，则动态加载
        if (!window.dataLayer) {
          this.loadGoogleTagManager()
        }
        
        // 启用GTM跟踪
        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'cookie_consent_update',
            'performance_cookies': 'granted'
          })
        }
      }
    },

    // 禁用Google Tag Manager
    disableGoogleTagManager() {
      if (typeof window !== 'undefined' && window.dataLayer) {
        // 禁用GTM跟踪
        window.dataLayer.push({
          'event': 'cookie_consent_update',
          'performance_cookies': 'denied'
        })
      }
    },

    // 动态加载Google Tag Manager
    loadGoogleTagManager() {
      if (typeof window !== 'undefined' && !window.dataLayer) {
        // 创建GTM脚本
        const script = document.createElement('script')
        script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-M5CJV68B');
        `
        document.head.appendChild(script)
      }
    },
  },

  // 持久化配置
  // persist: {
  //   enabled: true,
  //   strategies: [
  //     {
  //       key: 'cookie-settings',
  //       storage: localStorage,
  //       paths: ['cookieItems', 'hasUserConsent', 'lastUpdated', 'version']
  //     }
  //   ]
  // }
}) 